// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Paper from '@mui/material/Paper'

/** D-UI-3 — an outlined card with a title and one line of context. */
export default function Section({ title, subtitle, children }: { title: ReactNode; subtitle?: ReactNode; children: ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box component="h3" sx={{ m: 0, fontSize: 14, fontWeight: 700 }}>{title}</Box>
      {subtitle && <Box sx={{ mt: 0.25, mb: 1.25, fontSize: 12, color: 'text.secondary' }}>{subtitle}</Box>}
      {children}
    </Paper>
  )
}

/**
 * A small outlined KPI tile: label, big value, optional hint.
 *
 * With `onClick` the tile is a toggle (CareRx wave-1 #2): a real <button> — keyboard focus,
 * Enter / Space — whose `aria-pressed` carries `active`. A clickable tile must filter its page to
 * exactly the set its value counts; the caller owns that mapping.
 */
export function StatTile({ label, value, hint, color, onClick, active }: {
  label: string; value: ReactNode; hint?: ReactNode; color?: string
  onClick?: () => void; active?: boolean
}) {
  const body = (
    <>
      <Box sx={{ fontSize: 10.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.03em' }}>{label}</Box>
      <Box sx={{ fontSize: 20, fontWeight: 800, color, fontVariantNumeric: 'tabular-nums' }}>{value}</Box>
      {hint && <Box sx={{ fontSize: 11, color: 'text.secondary' }}>{hint}</Box>}
    </>
  )
  if (!onClick) return <Paper variant="outlined" sx={{ p: 1.5, flex: '1 1 150px', minWidth: 150 }}>{body}</Paper>
  return (
    <Paper variant="outlined" sx={{ flex: '1 1 150px', minWidth: 150, display: 'flex',
      borderColor: active ? 'primary.main' : undefined, bgcolor: active ? 'action.selected' : undefined }}>
      <ButtonBase onClick={onClick} aria-pressed={!!active}
        sx={{ flex: 1, display: 'block', textAlign: 'left', p: 1.5, borderRadius: 'inherit', font: 'inherit',
              '&:hover': { bgcolor: 'action.hover' },
              '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 } }}>
        {body}
      </ButtonBase>
    </Paper>
  )
}
